import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  DollarSign, TrendingUp, Target, Users, Award, Briefcase,
  ChevronLeft, Save, FileText, Calendar, ArrowUpRight, ArrowDownRight,
  PieChart as PieChartIcon, Activity, Loader2, Building2, GraduationCap,
} from "lucide-react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, Tooltip as RTooltip, Legend,
  LineChart, Line, XAxis, YAxis, CartesianGrid, BarChart, Bar,
} from "recharts";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

const COLORS = ["#009640", "#F59E0B", "#0F2A42", "#B45309", "#64748b"];

/**
 * AdminFinance — Finance & Revenus AFRICTAL (V3.4 additif).
 * Toutes les données proviennent des collections réelles. Aucune simulation.
 */
export default function AdminFinance() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super-admin"].includes(user?.role);
  const [tab, setTab] = useState("overview");
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [revenues, setRevenues] = useState(null);
  const [split, setSplit] = useState(null);
  const [forecast, setForecast] = useState(null);
  const [goals, setGoals] = useState(null);
  const [progress, setProgress] = useState(null);
  const [ts, setTs] = useState(null);
  const [monthly, setMonthly] = useState(null);
  const [annual, setAnnual] = useState(null);
  const [saving, setSaving] = useState(false);
  const [goalForm, setGoalForm] = useState({ monthly_goal: "", annual_goal: "", currency: "XOF" });
  const [splitScope, setSplitScope] = useState("this_year");
  const [tsDays, setTsDays] = useState(30);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, r, s, f, g, p, t, m, a] = await Promise.all([
        api.get("/v1/finance/overview"),
        api.get("/v1/finance/revenues"),
        api.get("/v1/finance/revenues/split", { params: { scope: splitScope } }),
        api.get("/v1/finance/forecast"),
        api.get("/v1/finance/goals"),
        api.get("/v1/finance/goals/progress"),
        api.get("/v1/finance/timeseries", { params: { days: tsDays } }),
        api.get("/v1/finance/reports/monthly"),
        api.get("/v1/finance/reports/annual"),
      ]);
      setOverview(o.data);
      setRevenues(r.data);
      setSplit(s.data);
      setForecast(f.data);
      setGoals(g.data.goals);
      setGoalForm({
        monthly_goal: g.data.goals.monthly_goal || "",
        annual_goal: g.data.goals.annual_goal || "",
        currency: g.data.goals.currency || "XOF",
      });
      setProgress(p.data);
      setTs(t.data);
      setMonthly(m.data);
      setAnnual(a.data);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, [splitScope, tsDays]);

  useEffect(() => { if (isAdmin) load(); }, [isAdmin, load]);

  const saveGoals = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {};
      if (goalForm.monthly_goal !== "") payload.monthly_goal = parseFloat(goalForm.monthly_goal);
      if (goalForm.annual_goal !== "") payload.annual_goal = parseFloat(goalForm.annual_goal);
      if (goalForm.currency) payload.currency = goalForm.currency;
      const { data } = await api.patch("/v1/finance/goals", payload);
      setGoals(data.goals);
      toast.success("Objectifs mis à jour");
      // reload progress
      const { data: p } = await api.get("/v1/finance/goals/progress");
      setProgress(p);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  if (!isAdmin) {
    return (
      <PrivateLayout title="Finance & Revenus">
        <div className="afr-empty" data-testid="finance-forbidden">
          <div className="afr-empty__title">Accès restreint</div>
          <div className="afr-empty__lead">Réservé aux Super Administrateurs.</div>
        </div>
      </PrivateLayout>
    );
  }

  const currency = goals?.currency || "XOF";

  return (
    <PrivateLayout
      title="💰 Finance & Revenus AFRICTAL"
      breadcrumbs={[{ to: "/admin", label: "Admin" }, { label: "Finance & Revenus" }]}
      actions={<Link to="/admin" className="afr-btn afr-btn--outline afr-btn--sm"><ChevronLeft size={14} /> Admin</Link>}
    >
      <div style={{ display: "flex", gap: 6, marginBottom: 20, borderBottom: "1px solid var(--afr-border)", flexWrap: "wrap" }} data-testid="finance-tabs">
        {[
          { k: "overview", label: "Vue d'ensemble", Icon: DollarSign },
          { k: "revenues", label: "Revenus par source", Icon: PieChartIcon },
          { k: "forecast", label: "Prévisions & Objectifs", Icon: Target },
          { k: "trend", label: "Tendance", Icon: Activity },
          { k: "reports", label: "Rapports", Icon: FileText },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => setTab(t.k)}
            data-testid={`finance-tab-${t.k}`}
            style={{
              padding: "10px 14px", border: 0, background: "transparent", cursor: "pointer",
              borderBottom: tab === t.k ? "3px solid var(--afr-primary)" : "3px solid transparent",
              color: tab === t.k ? "var(--afr-secondary)" : "#64748b",
              fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6,
            }}
          >
            <t.Icon size={14} /> {t.label}
          </button>
        ))}
      </div>

      {loading && <div data-testid="finance-loading">Chargement…</div>}

      {!loading && tab === "overview" && overview && revenues && (
        <OverviewTab overview={overview} revenues={revenues} progress={progress} currency={currency} />
      )}
      {!loading && tab === "revenues" && revenues && split && (
        <RevenuesTab revenues={revenues} split={split} splitScope={splitScope} setSplitScope={setSplitScope} currency={currency} />
      )}
      {!loading && tab === "forecast" && forecast && progress && (
        <ForecastTab forecast={forecast} progress={progress} goals={goals} goalForm={goalForm} setGoalForm={setGoalForm} saveGoals={saveGoals} saving={saving} currency={currency} />
      )}
      {!loading && tab === "trend" && ts && (
        <TrendTab ts={ts} tsDays={tsDays} setTsDays={setTsDays} currency={currency} />
      )}
      {!loading && tab === "reports" && monthly && annual && (
        <ReportsTab monthly={monthly} annual={annual} currency={currency} />
      )}
    </PrivateLayout>
  );
}

function OverviewTab({ overview, revenues, progress, currency }) {
  const rbp = revenues.by_period;
  return (
    <div data-testid="finance-overview">
      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 12, marginBottom: 20 }}>
        <Kpi Icon={DollarSign} label="CA aujourd'hui" value={fmt(rbp.today.total, currency)} accent="#009640" />
        <Kpi Icon={Calendar} label="Cette semaine" value={fmt(rbp.this_week.total, currency)} accent="#F59E0B" />
        <Kpi Icon={Calendar} label="Ce mois" value={fmt(rbp.this_month.total, currency)} accent="#0F2A42" hint={progress ? `${progress.monthly.progress_pct.toFixed(1)}% de l'objectif` : ""} />
        <Kpi Icon={Calendar} label="Cette année" value={fmt(rbp.this_year.total, currency)} accent="#B45309" hint={progress ? `${progress.annual.progress_pct.toFixed(1)}% de l'objectif` : ""} />
      </div>

      <h3 style={{ marginTop: 20, fontFamily: "'Outfit', sans-serif" }}>Indicateurs clés</h3>
      <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 }} data-testid="finance-kpis">
        <SmallKpi Icon={Users} label="Talents" value={overview.users_by_role.talent || 0} />
        <SmallKpi Icon={Award} label="Freelances" value={overview.users_by_role.freelance || 0} />
        <SmallKpi Icon={Briefcase} label="Employeurs" value={overview.users_by_role.employeur || 0} />
        <SmallKpi Icon={Building2} label="Entreprises" value={overview.companies_count || 0} />
        <SmallKpi Icon={GraduationCap} label="Universités" value={overview.users_by_role.universite || 0} />
        <SmallKpi Icon={TrendingUp} label="Investisseurs" value={overview.users_by_role.investisseur || 0} />
        <SmallKpi Icon={Award} label="Missions Certifiées" value={overview.certified_missions} accent="#15803D" />
        <SmallKpi Icon={Briefcase} label="Missions Externes" value={overview.external_missions} accent="#64748b" />
        <SmallKpi Icon={Briefcase} label="Offres publiées" value={overview.offers_total} />
        <SmallKpi Icon={Briefcase} label="Missions plateforme" value={overview.missions_total} />
        <SmallKpi Icon={Users} label="Abonnés actifs" value={overview.active_subscribers} />
        <SmallKpi Icon={TrendingUp} label="Conversion Emp.→Abo" value={`${overview.employer_to_subscriber_conversion_rate_pct}%`} accent="#B45309" />
      </div>
    </div>
  );
}

function RevenuesTab({ revenues, split, splitScope, setSplitScope, currency }) {
  const data = (split.items || []).filter((i) => i.amount > 0).map((i) => ({ name: i.source, value: i.amount }));
  const rbp = revenues.by_period.this_month;
  return (
    <div data-testid="finance-revenues">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 8 }}>
        <h3 style={{ fontFamily: "'Outfit', sans-serif", margin: 0 }}>Répartition des revenus</h3>
        <select value={splitScope} onChange={(e) => setSplitScope(e.target.value)} className="afr-input" data-testid="split-scope" style={{ maxWidth: 220 }}>
          <option value="today">Aujourd'hui</option>
          <option value="this_week">Cette semaine</option>
          <option value="this_month">Ce mois</option>
          <option value="this_year">Cette année</option>
          <option value="all_time">Depuis le début</option>
        </select>
      </div>

      <div className="afr-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <article className="afr-card" style={{ padding: 16, minHeight: 320 }} data-testid="revenues-piechart">
          <strong>Répartition · Total : {fmt(split.total, currency)}</strong>
          {data.length === 0 ? (
            <div className="afr-muted" style={{ padding: 40, textAlign: "center" }}>Aucun revenu sur la période sélectionnée</div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={90} label>
                  {data.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <RTooltip formatter={(v) => fmt(v, currency)} />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          )}
        </article>

        <article className="afr-card" style={{ padding: 16 }} data-testid="revenues-detail">
          <strong>Détail sources — ce mois</strong>
          <table className="afr-table" style={{ marginTop: 8 }}>
            <thead><tr><th>Source</th><th style={{ textAlign: "right" }}>Montant</th></tr></thead>
            <tbody>
              <tr><td>Abonnements</td><td style={{ textAlign: "right" }}>{fmt(rbp.subscriptions, currency)}</td></tr>
              <tr><td>Freelance (commissions)</td><td style={{ textAlign: "right", color: "#15803D", fontWeight: 700 }}>{fmt(rbp.freelance_commissions, currency)}</td></tr>
              <tr><td>Marketplace</td><td style={{ textAlign: "right" }}>{fmt(rbp.marketplace_commissions, currency)}</td></tr>
              <tr><td>Formations</td><td style={{ textAlign: "right" }}>{fmt(rbp.formations, currency)}</td></tr>
              <tr><td>Autres</td><td style={{ textAlign: "right" }}>{fmt(rbp.other, currency)}</td></tr>
              <tr style={{ borderTop: "2px solid #009640", background: "rgba(0,150,64,0.06)" }}>
                <td><strong>Total</strong></td><td style={{ textAlign: "right", fontWeight: 700 }}>{fmt(rbp.total, currency)}</td>
              </tr>
            </tbody>
          </table>
        </article>
      </div>

      {Object.keys(revenues.subscribers_by_plan || {}).length > 0 && (
        <article className="afr-card" style={{ padding: 16, marginTop: 16 }} data-testid="subscribers-by-plan">
          <strong>Abonnés actifs par plan</strong>
          <table className="afr-table" style={{ marginTop: 8 }}>
            <thead><tr><th>Plan</th><th style={{ textAlign: "right" }}>Nombre</th><th style={{ textAlign: "right" }}>CA cumulé</th></tr></thead>
            <tbody>
              {Object.entries(revenues.subscribers_by_plan).map(([plan, v]) => (
                <tr key={plan}><td style={{ textTransform: "capitalize" }}>{plan}</td><td style={{ textAlign: "right" }}>{v.count}</td><td style={{ textAlign: "right" }}>{fmt(v.revenue, currency)}</td></tr>
              ))}
            </tbody>
          </table>
        </article>
      )}
    </div>
  );
}

function ForecastTab({ forecast, progress, goalForm, setGoalForm, saveGoals, saving, currency }) {
  return (
    <div data-testid="finance-forecast">
      <div className="afr-grid" style={{ gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <ForecastCard title="Projection fin de mois" data={forecast.month} progress={progress.monthly} keyLabel="projection_end_of_month" currency={currency} testid="forecast-month" />
        <ForecastCard title="Projection fin d'année" data={forecast.year} progress={progress.annual} keyLabel="projection_end_of_year" currency={currency} testid="forecast-year" />
      </div>

      <article className="afr-card" style={{ padding: 20, marginTop: 20 }} data-testid="goals-form">
        <h3 style={{ marginTop: 0, fontFamily: "'Outfit', sans-serif" }}><Target size={16} style={{ verticalAlign: "middle" }} /> Objectifs financiers</h3>
        <p className="afr-muted" style={{ fontSize: "0.86rem" }}>Définissez vos objectifs. La progression et la projection s'affichent automatiquement.</p>
        <form onSubmit={saveGoals}>
          <div className="afr-grid" style={{ gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
            <Field label="Objectif mensuel" testid="goal-monthly">
              <input type="number" min="0" step="1" value={goalForm.monthly_goal} onChange={(e) => setGoalForm((f) => ({ ...f, monthly_goal: e.target.value }))} placeholder="5000000" />
            </Field>
            <Field label="Objectif annuel" testid="goal-annual">
              <input type="number" min="0" step="1" value={goalForm.annual_goal} onChange={(e) => setGoalForm((f) => ({ ...f, annual_goal: e.target.value }))} placeholder="60000000" />
            </Field>
            <Field label="Devise" testid="goal-currency">
              <input value={goalForm.currency} onChange={(e) => setGoalForm((f) => ({ ...f, currency: e.target.value }))} />
            </Field>
          </div>
          <button type="submit" disabled={saving} className="afr-btn afr-btn--primary" data-testid="goals-save">
            {saving ? <Loader2 size={14} className="afr-spin" /> : <Save size={14} />} Enregistrer les objectifs
          </button>
        </form>
      </article>
    </div>
  );
}

function ForecastCard({ title, data, progress, keyLabel, currency, testid }) {
  const reach = progress.will_reach;
  const pct = Math.min(100, progress.progress_pct);
  return (
    <article className="afr-card" data-testid={testid} style={{ padding: 18, borderLeft: `4px solid ${reach ? "#15803D" : "#B45309"}` }}>
      <strong>{title}</strong>
      <div style={{ marginTop: 10 }}>
        <div className="afr-muted" style={{ fontSize: "0.82rem" }}>Objectif</div>
        <div style={{ fontSize: "1.1rem", fontWeight: 700 }}>{fmt(progress.target, currency)}</div>
      </div>
      <div style={{ marginTop: 8 }}>
        <div className="afr-muted" style={{ fontSize: "0.82rem" }}>Réalisé</div>
        <div style={{ fontSize: "1.1rem", color: "#15803D", fontWeight: 700 }}>{fmt(progress.realized, currency)}</div>
      </div>
      <div style={{ marginTop: 8 }}>
        <div className="afr-muted" style={{ fontSize: "0.82rem" }}>Restant</div>
        <div style={{ fontSize: "1rem" }}>{fmt(progress.remaining, currency)}</div>
      </div>
      <div style={{ marginTop: 10 }}>
        <div className="afr-muted" style={{ fontSize: "0.82rem", display: "flex", justifyContent: "space-between" }}>
          <span>Progression</span><span>{progress.progress_pct.toFixed(1)}%</span>
        </div>
        <div style={{ background: "#e2e8f0", height: 10, borderRadius: 5, overflow: "hidden", marginTop: 4 }}>
          <div style={{ background: reach ? "#15803D" : "#F59E0B", width: `${pct}%`, height: "100%", transition: "width 0.3s" }} />
        </div>
      </div>
      <div style={{ marginTop: 12, padding: 10, background: reach ? "rgba(21,128,61,0.08)" : "rgba(180,83,9,0.08)", borderRadius: 8 }}>
        <div className="afr-muted" style={{ fontSize: "0.82rem" }}>Projection selon rythme actuel</div>
        <div style={{ fontSize: "1.05rem", fontWeight: 700, color: reach ? "#15803D" : "#B45309" }}>{fmt(data[keyLabel], currency)}</div>
        <div style={{ fontSize: "0.78rem", marginTop: 4, display: "flex", alignItems: "center", gap: 4 }}>
          {reach ? <><ArrowUpRight size={12} color="#15803D" /> Objectif atteignable</> : <><ArrowDownRight size={12} color="#B45309" /> En dessous de l'objectif</>}
          {" · "}Rythme quotidien : {fmt(data.daily_rate, currency)}
        </div>
      </div>
    </article>
  );
}

function TrendTab({ ts, tsDays, setTsDays, currency }) {
  const data = ts.points.map((p) => ({
    date: p.date.slice(5), // MM-DD
    Total: p.total,
    Commissions: p.commissions,
    Abonnements: p.subscriptions,
    Formations: p.formations,
  }));
  return (
    <div data-testid="finance-trend">
      <div style={{ display: "flex", gap: 6, marginBottom: 12 }} data-testid="trend-range">
        {[14, 30, 90, 365].map((d) => (
          <button
            key={d}
            type="button"
            onClick={() => setTsDays(d)}
            className={`afr-btn afr-btn--sm ${tsDays === d ? "afr-btn--primary" : "afr-btn--outline"}`}
            data-testid={`trend-${d}d`}
          >
            {d} jours
          </button>
        ))}
      </div>
      <article className="afr-card" style={{ padding: 16 }}>
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={data}>
            <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
            <XAxis dataKey="date" style={{ fontSize: 11 }} />
            <YAxis style={{ fontSize: 11 }} />
            <RTooltip formatter={(v) => fmt(v, currency)} />
            <Legend />
            <Line type="monotone" dataKey="Total" stroke="#0F2A42" strokeWidth={3} dot={false} />
            <Line type="monotone" dataKey="Commissions" stroke="#009640" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="Abonnements" stroke="#F59E0B" strokeWidth={2} dot={false} />
            <Line type="monotone" dataKey="Formations" stroke="#B45309" strokeWidth={2} dot={false} />
          </LineChart>
        </ResponsiveContainer>
      </article>
    </div>
  );
}

function ReportsTab({ monthly, annual, currency }) {
  const evo = monthly.evolution_pct;
  const growth = annual.growth_pct;
  const barData = annual.monthly_breakdown.map((m) => ({
    month: m.label.slice(0, 3),
    Abo: m.subscriptions,
    Freelance: m.freelance_commissions,
    Formations: m.formations,
    total: m.total,
  }));
  const bestSources = annual.best_sources.filter((s) => s.amount > 0);
  return (
    <div data-testid="finance-reports">
      <article className="afr-card" style={{ padding: 20, marginBottom: 20 }} data-testid="report-monthly">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
          <div>
            <h3 style={{ marginTop: 0, fontFamily: "'Outfit', sans-serif" }}>📅 Rapport mensuel — {monthly.period.label}</h3>
            <div className="afr-muted" style={{ fontSize: "0.86rem" }}>Généré le {new Date(monthly.generated_at).toLocaleString("fr-FR")}</div>
          </div>
          {evo !== null && (
            <span className="afr-badge" style={{
              background: evo >= 0 ? "rgba(21,128,61,0.12)" : "rgba(185,28,28,0.10)",
              color: evo >= 0 ? "#15803D" : "#B91C1C", fontWeight: 700,
            }}>
              {evo >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />} {Math.abs(evo).toFixed(1)}% vs mois précédent
            </span>
          )}
        </div>
        <div className="afr-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 10, marginTop: 12 }}>
          <SmallKpi Icon={DollarSign} label="CA du mois" value={fmt(monthly.revenue.total, currency)} accent="#0F2A42" />
          <SmallKpi Icon={Users} label="Nouveaux abonnés" value={monthly.new_subscribers} accent="#F59E0B" />
          <SmallKpi Icon={Award} label="Missions certifiées" value={monthly.certified_missions} accent="#15803D" />
          <SmallKpi Icon={GraduationCap} label="Formations vendues" value={monthly.formations_sold} accent="#B45309" />
          <SmallKpi Icon={TrendingUp} label="Commissions encaissées" value={fmt(monthly.africtal_commission_received, currency)} accent="#15803D" />
        </div>
        <details style={{ marginTop: 14 }}>
          <summary style={{ cursor: "pointer", fontWeight: 600 }}>Détail par source</summary>
          <table className="afr-table" style={{ marginTop: 8 }}>
            <thead><tr><th>Source</th><th style={{ textAlign: "right" }}>Ce mois</th><th style={{ textAlign: "right" }}>Mois précédent</th></tr></thead>
            <tbody>
              <tr><td>Abonnements</td><td style={{ textAlign: "right" }}>{fmt(monthly.revenue.subscriptions, currency)}</td><td style={{ textAlign: "right" }}>{fmt(monthly.revenue_previous_month.subscriptions, currency)}</td></tr>
              <tr><td>Freelance</td><td style={{ textAlign: "right" }}>{fmt(monthly.revenue.freelance_commissions, currency)}</td><td style={{ textAlign: "right" }}>{fmt(monthly.revenue_previous_month.freelance_commissions, currency)}</td></tr>
              <tr><td>Formations</td><td style={{ textAlign: "right" }}>{fmt(monthly.revenue.formations, currency)}</td><td style={{ textAlign: "right" }}>{fmt(monthly.revenue_previous_month.formations, currency)}</td></tr>
            </tbody>
          </table>
        </details>
      </article>

      <article className="afr-card" style={{ padding: 20 }} data-testid="report-annual">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
          <div>
            <h3 style={{ marginTop: 0, fontFamily: "'Outfit', sans-serif" }}>📊 Rapport annuel — {annual.year}</h3>
            <div className="afr-muted" style={{ fontSize: "0.86rem" }}>CA total : <strong>{fmt(annual.revenue.total, currency)}</strong></div>
          </div>
          {growth !== null && (
            <span className="afr-badge" style={{
              background: growth >= 0 ? "rgba(21,128,61,0.12)" : "rgba(185,28,28,0.10)",
              color: growth >= 0 ? "#15803D" : "#B91C1C", fontWeight: 700,
            }}>
              {growth >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />} {Math.abs(growth).toFixed(1)}% vs année précédente
            </span>
          )}
        </div>

        <div style={{ marginTop: 14 }}>
          <strong>Évolution mensuelle</strong>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={barData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="month" style={{ fontSize: 11 }} />
              <YAxis style={{ fontSize: 11 }} />
              <RTooltip formatter={(v) => fmt(v, currency)} />
              <Legend />
              <Bar dataKey="Abo" stackId="a" fill="#F59E0B" />
              <Bar dataKey="Freelance" stackId="a" fill="#009640" />
              <Bar dataKey="Formations" stackId="a" fill="#B45309" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={{ marginTop: 14 }}>
          <strong>Meilleures sources de revenus</strong>
          {bestSources.length === 0 ? (
            <div className="afr-muted" style={{ padding: 10 }}>Aucun revenu enregistré cette année.</div>
          ) : (
            <ol style={{ paddingLeft: 20, marginTop: 6 }}>
              {bestSources.map((s, i) => (
                <li key={s.source} style={{ marginBottom: 4 }}><strong>{s.source}</strong> — {fmt(s.amount, currency)}</li>
              ))}
            </ol>
          )}
        </div>

        <p className="afr-muted" style={{ fontSize: "0.82rem", marginTop: 14 }}>
          🗎 L'export PDF et Excel seront disponibles dans une prochaine version.
        </p>
      </article>
    </div>
  );
}

function Kpi({ Icon, label, value, accent, hint }) {
  return (
    <div className="afr-kpi" style={{ borderLeft: `3px solid ${accent}` }}>
      <div className="afr-kpi__icon" style={{ background: `color-mix(in oklab, ${accent} 14%, transparent)`, color: accent }}>
        <Icon size={18} />
      </div>
      <span className="afr-kpi__label">{label}</span>
      <span className="afr-kpi__value">{value}</span>
      {hint && <span className="afr-kpi__delta">{hint}</span>}
    </div>
  );
}

function SmallKpi({ Icon, label, value, accent = "#0F2A42" }) {
  return (
    <div className="afr-card" style={{ padding: 12, borderLeft: `3px solid ${accent}` }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div className="afr-muted" style={{ fontSize: "0.75rem" }}>{label}</div>
        {Icon && <Icon size={14} color={accent} />}
      </div>
      <div style={{ fontSize: "1.15rem", fontWeight: 700, marginTop: 4, color: "var(--afr-secondary)" }}>{value}</div>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="afr-card" style={{ padding: "0.9rem", display: "block" }} data-testid={testid}>
      <span className="afr-muted" style={{ fontSize: "0.78rem" }}>{label}</span>
      <div style={{ marginTop: "0.35rem" }}>
        <style>{`
          [data-testid='${testid}'] input {
            width: 100%; border: none; outline: none; background: transparent;
            font-family: 'Outfit', sans-serif; font-size: 0.95rem; color: var(--afr-secondary);
          }
        `}</style>
        {children}
      </div>
    </label>
  );
}

function fmt(n, cur = "XOF") {
  if (n == null || Number.isNaN(n)) return "—";
  try { return `${new Intl.NumberFormat("fr-FR").format(n)} ${cur}`; } catch { return `${n} ${cur}`; }
}
